import React, { FC, useMemo, useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import BoltIcon from '@mui/icons-material/Bolt';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { Certification, CertHouseholdData } from 'src/types/Certification';
import { certStatusState } from './householdSurveyUtils';
import { getUserAvatarColors } from 'src/components/NewRenterInterface/utils/avatarColors';
import LockedByPropertyModal from './LockedByPropertyModal';
import CertStatusModal from '../CertStatus/CertStatusModal';

function getCertStatus(cert: Certification): 'active' | 'inactive' {
  return cert.status?.renter_active === false ? 'inactive' : 'active';
}

/** Derive initials from first and last name */
function getMemberInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

/** Minimum width shared by the status chips so the card's right column stays
 *  a consistent size regardless of which chip label is shown. */
const STATUS_CHIP_MIN_WIDTH = 140;

interface HouseholdCardProps {
  cert: Certification;
  onClick?: (household: CertHouseholdData) => void;
  /** When provided, clicking the AvatarGroup opens HouseholdCompositionModal for this cert. */
  onAvatarGroupClick?: (cert: Certification) => void;
  /** When true, the card disables interaction and replaces the chevron with a
   *  spinner. Used by RenterHomePage while it resolves the next URL via
   *  fetchContinueCertification. */
  isLoading?: boolean;
  /** Called when the user taps "Continue" on the LockedByPropertyModal. */
  onLockedContinue?: (household: CertHouseholdData) => void;
}

const CertificationCard: FC<HouseholdCardProps> = ({
  cert,
  onClick,
  onAvatarGroupClick,
  isLoading = false,
  onLockedContinue,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  // Below ~425px the standardized chip width is too wide to sit beside the text,
  // so shrink it and let the chip label wrap onto multiple lines.
  const isNarrow = useMediaQuery(theme.breakpoints.down(425));
  const chipWidth = isNarrow ? 120 : STATUS_CHIP_MIN_WIDTH;
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const selectedHomePageMemberId = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.selectedHomePageMemberId
  );
  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  const certStatusLoading = useSelector(
    (state: RootState) => state.newRenterInterface.certStatusLoading
  );

  // Use the selected home page member when viewing another member's perspective.
  const effectiveUserId = selectedHomePageMemberId ?? user?.id;

  const certHousehold = cert.cert_household;
  const property = cert.property;
  const currentMember = certHousehold?.cert_members?.find(
    (m) => m.user_id === effectiveUserId
  );
  const currentMemberId = currentMember?.cert_member_id;

  // Whether the logged-in user (not the proxy-selected member) holds a managing
  // role in this household. Gates the edit pencil; non-managing members can
  // still open the household for a read-only view.
  const currentUserIsManaging =
    certHousehold?.cert_members?.some(
      (m) => m.user_id === user?.id && m.is_managing
    ) ?? false;

  const baseStatus = getCertStatus(cert);
  const isLocked = baseStatus === 'inactive';
  const statusDetail = certStatus[cert.id];
  const certState =
    baseStatus === 'active'
      ? certStatusState(statusDetail, effectiveUserId, currentMemberId)
      : 'review';
  const needsAction = certState === 'action';
  const statusLoaded = statusDetail !== undefined;

  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const handleOpenStatus = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setShowStatusModal(true);
  };

  const categoryName = cert.category?.name ?? '';
  const effectiveDate = cert.effective_date;
  const unitName = cert.unit?.name;
  const logNumber = cert.log_number;

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return '';
    }
    // Split YYYY-MM-DD into parts and construct a local date to avoid
    // UTC midnight shifting the date back by one day in negative-offset timezones.
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const members = useMemo(() => {
    const raw = certHousehold?.cert_members ?? [];
    return [...raw].sort((a, b) => {
      const aIsHelper = a.is_helper === true;
      const bIsHelper = b.is_helper === true;
      if (aIsHelper === bIsHelper) {
        return 0;
      }
      return aIsHelper ? 1 : -1;
    });
  }, [certHousehold?.cert_members]);

  return (
    <>
      <Card
        onClick={
          !isLoading
            ? () => {
                if (isLocked) {
                  setShowLockedModal(true);
                } else if (
                  !needsAction &&
                  statusLoaded &&
                  onLockedContinue &&
                  certHousehold
                ) {
                  onLockedContinue(certHousehold);
                } else if (onClick && certHousehold) {
                  onClick(certHousehold);
                }
              }
            : undefined
        }
        aria-busy={isLoading || undefined}
        elevation={0}
        sx={{
          p: '16px 16px 14px 16px',
          cursor: isLoading
            ? 'wait'
            : onClick || isLocked
            ? 'pointer'
            : 'default',
          opacity: isLoading ? 0.7 : 1,
          pointerEvents: isLoading ? 'none' : 'auto',
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
          border: 'none',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          ...((onClick || isLocked) &&
            !isLoading && {
              '&:hover': {
                boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
              },
            }),
        }}
      >
        {/* Left column */}
        <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
          {categoryName && (
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
              noWrap={!isMobile}
              sx={{ fontSize: 15, lineHeight: 1.3 }}
            >
              {categoryName}
            </Typography>
          )}
          {effectiveDate && (
            <Typography variant="caption" color="text.secondary">
              {formatDate(effectiveDate)}
            </Typography>
          )}
          {property?.name && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {property.name}
            </Typography>
          )}
          {(unitName || logNumber) && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {unitName && (
                <Typography variant="caption" color="text.secondary">
                  Unit {unitName}
                </Typography>
              )}
              {unitName && logNumber && (
                <Typography variant="caption" color="text.secondary">
                  -
                </Typography>
              )}
              {logNumber && (
                <Typography variant="caption" color="text.secondary">
                  Log #{logNumber}
                </Typography>
              )}
            </Stack>
          )}
          {certHousehold?.name && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {certHousehold.name}
            </Typography>
          )}
        </Stack>

        {/* Right column */}
        <Stack alignItems="flex-start" gap={1} flexShrink={0}>
          {certStatusLoading && !isLocked ? (
            <Skeleton variant="rounded" width={chipWidth} height={28} />
          ) : isLocked ? (
            <Chip
              icon={<LockIcon sx={{ fontSize: '14px !important' }} />}
              label="Locked by Property"
              size="small"
              clickable
              onClick={handleOpenStatus}
              deleteIcon={
                <ChevronRightIcon sx={{ fontSize: '16px !important' }} />
              }
              onDelete={handleOpenStatus}
              sx={{
                backgroundColor: 'action.selected',
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: 12,
                minHeight: 30,
                minWidth: chipWidth,
                maxWidth: isNarrow ? chipWidth : 'none',
                borderRadius: '8px',
                ...(isNarrow && {
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.25,
                    py: 0.5,
                  },
                }),
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                transition: 'box-shadow 0.1s',
                '& .MuiChip-icon': { ml: 0.5, color: 'text.secondary' },
                '& .MuiChip-deleteIcon': {
                  color: 'inherit',
                  opacity: 0.7,
                  '&:hover': {
                    color: 'inherit',
                    backgroundColor: 'transparent',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.focus',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.22)',
                },
              }}
            />
          ) : needsAction ? (
            <Chip
              icon={<BoltIcon sx={{ fontSize: '14px !important' }} />}
              label="Take Action"
              size="small"
              clickable
              onClick={handleOpenStatus}
              deleteIcon={
                <ChevronRightIcon sx={{ fontSize: '16px !important' }} />
              }
              onDelete={handleOpenStatus}
              sx={{
                backgroundColor: 'error.lighter',
                color: 'error.dark',
                fontWeight: 500,
                fontSize: 12,
                minHeight: 30,
                minWidth: chipWidth,
                maxWidth: isNarrow ? chipWidth : 'none',
                borderRadius: '8px',
                ...(isNarrow && {
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.25,
                    py: 0.5,
                  },
                }),
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                transition: 'box-shadow 0.1s',
                '& .MuiChip-icon': { ml: 0.5, color: 'error.dark' },
                '& .MuiChip-deleteIcon': {
                  color: 'inherit',
                  opacity: 0.7,
                  '&:hover': {
                    color: 'inherit',
                    backgroundColor: 'transparent',
                  },
                },
                '&:hover': {
                  backgroundColor: 'error.light',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.22)',
                },
              }}
            />
          ) : statusLoaded ? (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={
                certState === 'decision'
                  ? 'Decision issued'
                  : 'No Action Needed'
              }
              size="small"
              clickable
              onClick={handleOpenStatus}
              deleteIcon={
                <ChevronRightIcon sx={{ fontSize: '16px !important' }} />
              }
              onDelete={handleOpenStatus}
              sx={{
                backgroundColor: 'success.lighter',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 12,
                minHeight: 30,
                minWidth: chipWidth,
                maxWidth: isNarrow ? chipWidth : 'none',
                borderRadius: '8px',
                ...(isNarrow && {
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    lineHeight: 1.25,
                    py: 0.5,
                  },
                }),
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                transition: 'box-shadow 0.1s',
                '& .MuiChip-icon': { ml: 0.5, color: 'primary.main' },
                '& .MuiChip-deleteIcon': {
                  color: 'inherit',
                  opacity: 0.7,
                  '&:hover': {
                    color: 'inherit',
                    backgroundColor: 'transparent',
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.24),
                  boxShadow: '0 3px 8px rgba(0,0,0,0.22)',
                },
              }}
            />
          ) : null}
          {members.length > 0 && (
            <Stack direction="column" alignItems="flex-start" gap={0.5}>
              {members.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ...(onAvatarGroupClick
                      ? {
                          cursor: 'pointer',
                          '&:hover .MuiAvatarGroup-root': { opacity: 0.85 },
                        }
                      : {}),
                  }}
                  onClick={
                    onAvatarGroupClick
                      ? (e: React.MouseEvent) => {
                          e.stopPropagation();
                          onAvatarGroupClick(cert);
                        }
                      : undefined
                  }
                >
                  <AvatarGroup
                    max={3}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        fontSize: 13,
                        fontWeight: 600,
                      },
                      '& .MuiAvatarGroup-avatar': { ml: '-8px' },
                      '& .MuiAvatarGroup-avatar:first-of-type': {
                        color: isDark
                          ? '#212B36 !important'
                          : '#FFFFFF !important',
                        backgroundColor: isDark
                          ? '#85C1E9 !important'
                          : '#1A5276 !important',
                      },
                    }}
                  >
                    {members.map((m) => {
                      const { bg, fg } = getUserAvatarColors(
                        m.user_id ?? m.cert_member_id,
                        isDark
                      );
                      const initials = getMemberInitials(
                        m.first_name,
                        m.last_name
                      );
                      const avatar = (
                        <Avatar
                          key={m.cert_member_id}
                          sx={{
                            backgroundColor: `${bg} !important`,
                            color: `${fg} !important`,
                            width: 32,
                            height: 32,
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {initials}
                        </Avatar>
                      );
                      return avatar;
                    })}
                  </AvatarGroup>
                </Box>
              )}
              {onAvatarGroupClick && currentUserIsManaging && (
                <Button
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onAvatarGroupClick(cert);
                  }}
                  color="info.light"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 500,
                    color: isDark ? 'info.light' : 'info.dark',
                    minWidth: 0,
                    px: 1,
                    py: 0.5,
                    borderRadius: '8px',
                  }}
                >
                  Edit household
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        {/* Chevron — shown when card is interactive (has onClick or is locked).
          Replaced by a spinner while the parent resolves the next URL. */}
        {(onClick || isLocked) &&
          (isLoading ? (
            <CircularProgress
              size={20}
              sx={{ color: 'text.secondary', flexShrink: 0, mx: '5px' }}
              aria-label="Loading"
            />
          ) : (
            <ChevronRightIcon
              sx={{ fontSize: 30, color: 'grey.500', flexShrink: 0 }}
            />
          ))}
      </Card>

      <LockedByPropertyModal
        open={showLockedModal}
        onClose={() => setShowLockedModal(false)}
        onContinue={
          onLockedContinue && certHousehold
            ? () => {
                setShowLockedModal(false);
                onLockedContinue(certHousehold);
              }
            : undefined
        }
        propertyEmail={
          cert.property?.property_email ||
          `${cert.property?.slug ?? ''}@prontohousing.io`
        }
        propertyName={cert.property?.name}
      />

      <CertStatusModal
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        cert={cert}
        memberUserId={effectiveUserId ?? null}
      />
    </>
  );
};

export default CertificationCard;
